// apps/web/src/components/AnimatedLogisticsScene.tsx

import React, { useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars, Float, Line, Text } from '@react-three/drei';
import * as THREE from 'three';
import { useTheme } from 'next-themes';

const GLOBE_RADIUS = 12;
const DOT_COUNT = 6000;
const CONNECTION_COUNT = 15;

const ParticleGlobe = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const primaryColor = isDark ? new THREE.Color('#3b82f6') : new THREE.Color('#2563eb');

  const points = useMemo(() => {
    const p = new Float32Array(DOT_COUNT * 3);
    const phiSpan = Math.PI * 2;
    const thetaSpan = Math.PI;
    for (let i = 0; i < DOT_COUNT; i++) {
      const phi = Math.random() * phiSpan;
      const theta = Math.acos((Math.random() * 2) - 1);
      const r = GLOBE_RADIUS;
      const x = r * Math.sin(theta) * Math.cos(phi);
      const y = r * Math.sin(theta) * Math.sin(phi);
      const z = r * Math.cos(theta);
      p[i * 3] = x;
      p[i * 3 + 1] = y;
      p[i * 3 + 2] = z;
    }
    return p;
  }, []);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(points, 3));
    return geo;
  }, [points]);

  const groupRef = useRef<THREE.Group>(null!);
  useFrame(() => {
    if (groupRef.current) groupRef.current.rotation.y += 0.001;
  });

  return (
    <group ref={groupRef}>
      <points geometry={geometry}>
        <pointsMaterial size={0.12} color={primaryColor} transparent opacity={isDark ? 0.6 : 0.8} sizeAttenuation />
      </points>
      <mesh>
        <sphereGeometry args={[GLOBE_RADIUS * 0.98, 32, 32]} />
        <meshBasicMaterial color={isDark ? '#000000' : '#ffffff'} transparent opacity={0.9} />
      </mesh>
      <mesh scale={[1.1, 1.1, 1.1]}>
        <sphereGeometry args={[GLOBE_RADIUS, 32, 32]} />
        <meshBasicMaterial color={primaryColor} transparent opacity={0.05} side={THREE.BackSide} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
};

const DataArcs = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const arcs = useMemo(() => {
    const lines: { points: THREE.Vector3[] }[] = [];
    for (let i = 0; i < CONNECTION_COUNT; i++) {
      const phi1 = Math.random() * Math.PI * 2;
      const theta1 = Math.acos((Math.random() * 2) - 1);
      const start = new THREE.Vector3().setFromSphericalCoords(GLOBE_RADIUS, theta1, phi1);
      const phi2 = Math.random() * Math.PI * 2;
      const theta2 = Math.acos((Math.random() * 2) - 1);
      const end = new THREE.Vector3().setFromSphericalCoords(GLOBE_RADIUS, theta2, phi2);
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5).normalize().multiplyScalar(GLOBE_RADIUS * 1.5);
      const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
      lines.push({ points: curve.getPoints(40) });
    }
    return lines;
  }, []);

  const groupRef = useRef<THREE.Group>(null!);
  useFrame(() => {
    if (groupRef.current) groupRef.current.rotation.y += 0.001;
  });

  return (
    <group ref={groupRef}>
      {arcs.map((arc, i) => (
        <Line
          key={i}
          points={arc.points}
          color={isDark ? "#60a5fa" : "#2563eb"}
          lineWidth={1.5}
          transparent
          opacity={0.4}
        />
      ))}
    </group>
  );
}

const SatelliteNode = ({ offset, speed }: { offset: number, speed: number }) => {
  const ref = useRef<THREE.Group>(null!);
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed + offset;
    ref.current.position.x = Math.sin(t) * (GLOBE_RADIUS + 3);
    ref.current.position.z = Math.cos(t) * (GLOBE_RADIUS + 3);
    ref.current.position.y = Math.sin(t * 0.5) * 4;
    ref.current.lookAt(0, 0, 0);
  });

  return (
    <group ref={ref}>
      <mesh>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="#ec4899" emissive="#ec4899" emissiveIntensity={2} />
      </mesh>
      <pointLight distance={5} intensity={1} color="#ec4899" />
    </group>
  );
}

const MalawiMarker = () => {
  // Malawi: -13.25, 34.30
  const lat = -13.25;
  const lon = 34.30;
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const r = GLOBE_RADIUS;
  const x = -(r * Math.sin(phi) * Math.cos(theta));
  const z = (r * Math.sin(phi) * Math.sin(theta));
  const y = (r * Math.cos(phi));

  const ref = useRef<THREE.Mesh>(null!);
  useFrame((state) => {
    if (ref.current) {
      const s = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.3;
      ref.current.scale.set(s, s, 1);
    }
  });

  return (
    <group position={[x, y, z]} lookAt={new THREE.Vector3(0, 0, 0)}>
      <mesh ref={ref} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.2, 0.4, 32]} />
        <meshBasicMaterial color="#ef4444" transparent opacity={0.6} side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.5]}>
        <cylinderGeometry args={[0.05, 0.05, 1, 8]} />
        <meshBasicMaterial color="#ef4444" />
      </mesh>
      <mesh position={[0, 0, 1.2]}>
        <sphereGeometry args={[0.2]} />
        <meshBasicMaterial color="#ef4444" />
      </mesh>
      <Float speed={2} rotationIntensity={0} floatIntensity={0.2} position={[0, 1.5, 1.5]}>
        <Text
          fontSize={0.6}
          color="white"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.05}
          outlineColor="black"
          rotation={[0, Math.PI, 0]}
        >
          MALAWI HQ
        </Text>
      </Float>
    </group>
  );
}

const GlobalLogisticsScene = () => {
  return (
    <group>
      <ParticleGlobe />
      <DataArcs />
      <MalawiMarker />
      <SatelliteNode offset={0} speed={0.4} />
      <SatelliteNode offset={2} speed={0.3} />
      <SatelliteNode offset={4} speed={0.5} />
      <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
    </group>
  );
};

const AnimatedLogisticsScene = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <div className="absolute inset-0 w-full h-full cursor-move">
      <Canvas camera={{ position: [20, 15, 20], fov: 45 }} dpr={[1, 2]}>
        <color attach="background" args={[isDark ? '#020617' : '#f8fafc']} />
        <ambientLight intensity={isDark ? 0.2 : 0.6} />
        <pointLight position={[50, 50, 50]} intensity={1} color="#3b82f6" />
        <pointLight position={[-50, 0, -50]} intensity={1} color="#ec4899" />
        <spotLight position={[0, 50, 0]} angle={0.5} penumbra={1} intensity={1} castShadow />
        <Suspense fallback={null}>
          <GlobalLogisticsScene />
        </Suspense>
        <OrbitControls
          enableZoom={true}
          minDistance={15}
          maxDistance={40}
          autoRotate={true}
          autoRotateSpeed={0.5}
          enablePan={true}
        />
      </Canvas>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 pointer-events-none">
      </div>
    </div>
  );
};

export default AnimatedLogisticsScene;
